import React, { ReactNode, useCallback, useState } from "react";

import Switch from "antd/es/switch";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { defineMessages, useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import PinLegend, {
  NumericHistogramData,
  PinLegendRange,
} from "@mapmycustomers/shared/types/map/PinLegend";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import localSettings from "@app/config/LocalSettings";

import styles from "./CustomNumericShapeKey.module.scss";
import ShapeBadge from "./ShapeBadge";
import ShapeKey from "./ShapeKey";
import useNumericHistogramChartConfig from "./useNumericHistogramChartConfig";

const messages = defineMessages({
  displayChart: {
    id: "map.layer.shapeKey.legend.customNumeric.displayChartToggle",
    defaultMessage: "Display graph",
    description: "Label of the graph toggle switch in Custom Numeric Shape legend",
  },
  noStats: {
    id: "map.layer.shapeKey.legend.customNumeric.stats.noData",
    defaultMessage: "No data, please refresh the page to update.",
    description: "No Stats message in Custom Numeric Shape legend",
  },
  rangeLabel: {
    id: "map.layer.shapeKey.legend.customNumeric.rangeLabel",
    defaultMessage: "{min} to {max} <span>({count})</span>",
    description: "Range label in Custom Numeric Shape legend",
  },
  unknown: {
    id: "map.layer.shapeKey.legend.customNumeric.default",
    defaultMessage: "No value <span>({count})</span>",
    description: "A default option to show in a list of buckets for shape key",
  },
});

interface Props {
  entityType: EntityTypesSupportedByMapsPage;
  field: IField;
  pinLegend: PinLegend;
}

const CustomNumericShapeKey: React.FC<Props> = ({ field, pinLegend }) => {
  const intl = useIntl();

  const [chartVisible, setChartVisibility] = useState(false);
  const toggleChartVisibility = useCallback(() => setChartVisibility((visible) => !visible), []);
  const handleSwitchChange = useCallback(
    (checked: boolean, event: React.MouseEvent<HTMLButtonElement>) => {
      stopEvents(event);
      setChartVisibility(checked);
    },
    []
  );

  const valueFormatter = useCallback(
    (value: number) => {
      const currencyCode = localSettings.getCurrencyCode();
      return field.hasFeature(FieldFeature.MONETARY_VALUE)
        ? intl.formatNumber(value, {
            currency: currencyCode,
            maximumFractionDigits: 2,
            style: "currency",
          })
        : intl.formatNumber(value, {
            maximumFractionDigits: 2,
          });
    },
    [field, intl]
  );

  const chartConfig = useNumericHistogramChartConfig({
    data: (pinLegend.stats?.data?.histogram?.histogram as NumericHistogramData) ?? [],
    ranges: pinLegend.stats?.data.ranges,
    visible: chartVisible,
  });

  if (!pinLegend.stats) {
    return <ShapeKey>{intl.formatMessage(messages.noStats)}</ShapeKey>;
  }

  return (
    <div className={styles.container}>
      {chartVisible && (
        <HighchartsReact
          containerProps={{ className: styles.chart }}
          highcharts={Highcharts}
          isPureConfig
          options={chartConfig}
        />
      )}
      <div className={styles.buckets}>
        {pinLegend.thematic!.defaults?.shape && (
          <ShapeBadge
            key="unknown"
            label={intl.formatMessage(messages.unknown, {
              count: pinLegend.stats?.data?.missing ?? 0,
              span: (text: ReactNode[]) => <span className={styles.count}>{text}</span>,
            })}
            shape={pinLegend.thematic!.defaults.shape}
          />
        )}
        {pinLegend.stats.data.ranges.map(([min, max, count]: PinLegendRange, index: number) => (
          <ShapeBadge
            key={index}
            label={intl.formatMessage(messages.rangeLabel, {
              count,
              max: valueFormatter(max as number),
              min: valueFormatter(min as number),
              span: (text: ReactNode[]) => <span className={styles.count}>{text}</span>,
            })}
            shape={pinLegend.thematic!.shapes![index].shape}
          />
        ))}
      </div>
      {pinLegend.stats?.data?.histogram && (
        <div className={styles.chartToggle} onClick={toggleChartVisibility}>
          <span>{intl.formatMessage(messages.displayChart)}</span>
          <Switch checked={chartVisible} onChange={handleSwitchChange} />
        </div>
      )}
    </div>
  );
};

export default CustomNumericShapeKey;
